import z from 'zod';

import { assertNever } from '../assertNever';
import { zodDateOrString } from '../utils/Zod';
import { NonPerformanceCode } from './NonPerformanceCode';

export const NonPerformanceEventDate = z.discriminatedUnion('type', [
  /**
   * When this was created, the dates of the items were not adjusted to the correct timezone,
   * which meant we needed to discriminate between "plain dates" and "timestamps/instants" when
   * constructing the non-performance events
   * However, now that we've established the pre-requisite that the snapshot is adjusted by `translateLoanSnapshotToTimezone`
   * we know that all dates are correctly adjusted in the right timezone, so we only need "date" and "dateRange" types
   */
  z.object({
    type: z.literal('date'),
    date: zodDateOrString,
  }),
  z.object({
    type: z.literal('dateRange'),
    startDate: zodDateOrString,
    endDate: zodDateOrString.optional(),
  }),

  /**
   * These are the deprecated union members
   */
  z.object({
    type: z.literal('plainDateRange'),
    startDate: zodDateOrString,
    endDate: zodDateOrString.optional(),
  }),
  z.object({
    type: z.literal('plainDate'),
    date: zodDateOrString,
  }),
  z.object({
    type: z.literal('timestamp'),
    timestamp: zodDateOrString,
  }),
  z.object({
    type: z.literal('none'),
  }),
]);

export type NonPerformanceEventDate = z.infer<typeof NonPerformanceEventDate>;

export function nonPerformanceEventDatesAreEqual(
  event1: NonPerformanceEventDate,
  event2: NonPerformanceEventDate,
): boolean {
  switch (event1.type) {
    case 'dateRange':
      if (event2.type !== 'dateRange') {
        return false;
      }
      return (
        event1.startDate.getTime() === event2.startDate.getTime() &&
        event1.endDate?.getTime() === event2.endDate?.getTime()
      );
    case 'date':
      if (event2.type !== 'date') {
        return false;
      }
      return event1.date.getTime() === event2.date.getTime();

    // Deprecated date types
    case 'plainDateRange':
      if (event2.type !== 'plainDateRange') {
        return false;
      }
      return (
        event1.startDate.getTime() === event2.startDate.getTime() &&
        event1.endDate?.getTime() === event2.endDate?.getTime()
      );
    case 'plainDate':
      if (event2.type !== 'plainDate') {
        return false;
      }
      return event1.date.getTime() === event2.date.getTime();
    case 'timestamp':
      if (event2.type !== 'timestamp') {
        return false;
      }
      return event1.timestamp.getTime() === event2.timestamp.getTime();
    case 'none':
      if (event2.type !== 'none') {
        return false;
      }
      return true;
    default:
      return assertNever(event1);
  }
}

export const NonPerformanceEvent = z.object({
  code: NonPerformanceCode,
  date: NonPerformanceEventDate,
});

export type NonPerformanceEvent = z.infer<typeof NonPerformanceEvent>;

export function nonPerformanceEventSortFn(a: NonPerformanceEvent, b: NonPerformanceEvent): number {
  const dateA = getEventDateForSorting(a.date);
  const dateB = getEventDateForSorting(b.date);
  const dateDiff = dateA.getTime() - dateB.getTime();
  if (dateDiff !== 0) {
    return dateDiff;
  }
  return a.code.localeCompare(b.code);
}

function getEventDateForSorting(date: NonPerformanceEventDate): Date {
  switch (date.type) {
    case 'date':
      return date.date;
    case 'dateRange':
      return date.startDate;

    // Deprecated date types
    case 'plainDate':
      return date.date;
    case 'plainDateRange':
      return date.startDate;
    case 'timestamp':
      return date.timestamp;
    case 'none':
      return new Date(0); // Use epoch for 'none' type
    default:
      throw new Error(`Invalid event date type: ${(date as { type: string }).type}`);
  }
}
